import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Section } from '@/shared/ui/layout/Section';
import { DataTable } from '@/shared/ui/data-table/DataTable';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { StatusChip } from '@/shared/ui/chips/StatusChip';
import { FulfillmentChip } from '@/shared/ui/chips/FulfillmentChip';
import { PartyStatusChip } from '@/shared/ui/chips/PartyStatusChip';
import { extractErrorMessage } from '@/shared/api/errors';
import { useClientClaimedTrekIds } from '@/entities/client';
import { formatDate } from '@/shared/utils/date';
import type { ApiClaimedTrekIdItem } from '@/shared/types/api/clients';

type Props = {
  clientId: string;
};

export const ClaimedTrekIdsSection = ({ clientId }: Props) => {
  const { t } = useTranslation();
  const query = useClientClaimedTrekIds(clientId);

  const columns = useMemo<ColumnDef<ApiClaimedTrekIdItem>[]>(
    () => [
      {
        accessorKey: 'trek_id',
        header: t('clients.claimedTrekIds.columns.trekId'),
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          const text = (
            <span className="font-mono text-xs font-medium text-ink">{row.trek_id}</span>
          );
          if (row.parcel_id) {
            return (
              <Link
                to={`/parcels/${row.parcel_id}`}
                className="hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {text}
              </Link>
            );
          }
          return text;
        },
      },
      {
        id: 'parcel_status',
        header: t('clients.claimedTrekIds.columns.parcelStatus'),
        enableSorting: false,
        cell: (info) => {
          const s = info.row.original.parcel_status;
          return s ? <StatusChip status={s} /> : <span className="text-ink-faint">—</span>;
        },
      },
      {
        id: 'fulfillment_status',
        header: t('clients.claimedTrekIds.columns.fulfillmentStatus'),
        enableSorting: false,
        cell: (info) => {
          const s = info.row.original.fulfillment_status;
          return s ? <FulfillmentChip status={s} /> : <span className="text-ink-faint">—</span>;
        },
      },
      {
        id: 'party_status',
        header: t('clients.claimedTrekIds.columns.partyStatus'),
        enableSorting: false,
        cell: (info) => {
          const s = info.row.original.party_status;
          return s ? <PartyStatusChip status={s} /> : <span className="text-ink-faint">—</span>;
        },
      },
      {
        accessorKey: 'cargo_id_code',
        header: t('clients.claimedTrekIds.columns.cargoIdCode'),
        enableSorting: false,
        cell: (info) => {
          const code = info.row.original.cargo_id_code;
          return code ? (
            <span className="font-mono text-xs text-ink-soft">{code}</span>
          ) : (
            <span className="text-ink-faint">—</span>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: t('clients.claimedTrekIds.columns.createdAt'),
        enableSorting: false,
        cell: (info) => (
          <span className="text-xs text-ink-soft">{formatDate(info.row.original.created_at)}</span>
        ),
      },
    ],
    [t],
  );

  return (
    <Section title={t('clients.claimedTrekIds.title')}>
      {query.error ? (
        <ErrorState
          message={extractErrorMessage(query.error, t('clients.claimedTrekIds.loadError'))}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <DataTable
          data={query.data ?? []}
          columns={columns}
          isLoading={query.isLoading}
          emptyTitle={t('clients.claimedTrekIds.empty')}
          getRowId={(row, index) => row.trek_id || String(index)}
        />
      )}
    </Section>
  );
};
